/**
 * CodeLabPlayer — learner-facing live coding environment.
 *
 * Split layout: instructions (markdown) on the left, code editor on the right.
 * Supports running tests, submitting code, viewing results, hints, and
 * submission history. Responsive — stacks vertically on mobile.
 *
 * Sandbox providers:
 *   - 'local' (default): textarea + server-side code execution
 *   - 'stackblitz': embedded StackBlitz WebContainer via SandboxIframe
 *   - 'jupyterlite': in-browser Python notebook via Pyodide/WASM
 */
import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import SandboxIframe from '@shared/widgets/scenario_os/SandboxIframe';
import { emitLearnerEvent } from '../../services/learnerEvents';

const TOKEN_KEY = 'scenario_os_token';
const API_BASE = '/scenario-os/code-lab';

class SandboxErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      this.props.onError?.();
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function JupyterLiteGuide({ needsExternalPackages = true }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={guideContainerStyle}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={guideToggleStyle}
      >
        {expanded ? '\u25BE' : '\u25B8'} How to Use the AI Notebook
      </button>
      {expanded && (
        <div style={guideContentStyle}>
          <div style={guideSection}>
            <div style={guideSectionTitle}>Getting Started</div>
            <ol style={guideListStyle}>
              <li>The notebook opens with an empty code cell. Click inside it to start typing Python code.</li>
              <li>Type your code directly in the cell (e.g., <code style={guideCodeInline}>print("Hello, AI!")</code>).</li>
              <li>Press <strong>Shift + Enter</strong> to run the cell. Output appears below it.</li>
            </ol>
          </div>

          <div style={guideSection}>
            <div style={guideSectionTitle}>Writing and Running Code</div>
            <ul style={guideListStyle}>
              <li><strong>Run a cell:</strong> Click the cell, then press <strong>Shift + Enter</strong> (runs and moves to next cell) or <strong>Ctrl + Enter</strong> (runs and stays).</li>
              <li><strong>Add a new cell:</strong> Click the <strong>+</strong> button in the notebook toolbar, or press <strong>B</strong> when not editing a cell.</li>
              <li><strong>Delete a cell:</strong> Click the cell, then press <strong>D</strong> twice (dd) when not editing.</li>
              <li><strong>Undo:</strong> Press <strong>Ctrl + Z</strong> inside a cell to undo typing.</li>
            </ul>
          </div>

          <div style={guideSection}>
            <div style={guideSectionTitle}>Using the Starter Code</div>
            <ol style={guideListStyle}>
              <li>Look at the <strong>Starter Code (reference)</strong> section on the left panel.</li>
              <li>Click <strong>Copy to clipboard</strong> to copy the code.</li>
              <li>Click inside an empty notebook cell and paste (<strong>Ctrl + V</strong> / <strong>Cmd + V</strong>).</li>
              <li>Modify the code as instructed, then press <strong>Shift + Enter</strong> to run.</li>
            </ol>
          </div>

          <div style={guideSection}>
            <div style={guideSectionTitle}>Example: Try This Now</div>
            <pre style={guideCodeBlock}>
{`# Type this in a notebook cell and press Shift+Enter:
name = "AI Learner"
print(f"Welcome, {name}!")

# Try changing the name and running again
for i in range(3):
    print(f"Step {i+1}: Learning AI concepts")`}
            </pre>
          </div>

          <div style={guideSection}>
            <div style={guideSectionTitle}>Important Notes</div>
            <ul style={guideListStyle}>
              <li>This notebook runs <strong>Python in your browser</strong> (no installation needed).</li>
              {needsExternalPackages ? (
                <li>Some packages (like <code style={guideCodeInline}>langchain</code>, <code style={guideCodeInline}>openai</code>) are <strong>not available</strong> in the browser. Use the starter code as a reference for those.</li>
              ) : (
                <li>This lab uses the <strong>Python standard library only</strong> — it runs fully in the browser notebook.</li>
              )}
              <li>Available packages include: <code style={guideCodeInline}>numpy</code>, <code style={guideCodeInline}>pandas</code>, <code style={guideCodeInline}>matplotlib</code>, <code style={guideCodeInline}>json</code>, <code style={guideCodeInline}>re</code>, <code style={guideCodeInline}>math</code>.</li>
              <li>Your work is <strong>autosaved</strong> to your TeachIQ account while you have track access. Prefer copying critical cells before a hard refresh of the notebook iframe itself.</li>
              <li>If the notebook freezes, refresh the page and try again — your last autosaved draft will rehydrate into the reference editor / lab state.</li>
            </ul>
          </div>

          <div style={guideSection}>
            <div style={guideSectionTitle}>Keyboard Shortcuts</div>
            <table style={guideTableStyle}>
              <tbody>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>Shift + Enter</kbd></td><td>Run cell, move to next</td></tr>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>Ctrl + Enter</kbd></td><td>Run cell, stay in place</td></tr>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>B</kbd></td><td>Insert cell below (command mode)</td></tr>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>A</kbd></td><td>Insert cell above (command mode)</td></tr>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>D D</kbd></td><td>Delete selected cell (command mode)</td></tr>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>Esc</kbd></td><td>Enter command mode</td></tr>
                <tr><td style={guideKeyCell}><kbd style={kbdStyle}>Enter</kbd></td><td>Enter edit mode</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Simple Markdown → HTML renderer (no external dependency) ─────────────
function renderMarkdown(md) {
  if (!md) return '';
  let html = md
    // code blocks ```...```
    .replace(/```[\w]*\n?([\s\S]*?)```/g, (_, c) =>
      `<pre style="background:#1e1e2e;color:#e2e8f0;padding:12px;border-radius:6px;overflow:auto;font-size:12px;line-height:1.6;margin:8px 0"><code>${c.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</code></pre>`)
    // inline `code`
    .replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;color:#0f172a;padding:1px 5px;border-radius:4px;font-size:0.92em">$1</code>')
    // **bold**
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // *italic*
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // ### heading
    .replace(/^### (.+)$/gm, '<h4 style="font-size:13px;font-weight:700;color:#1e293b;margin:12px 0 4px">$1</h4>')
    // ## heading
    .replace(/^## (.+)$/gm, '<h3 style="font-size:15px;font-weight:700;color:#1e293b;margin:14px 0 6px;border-bottom:1px solid #e5e7eb;padding-bottom:4px">$1</h3>')
    // # heading
    .replace(/^# (.+)$/gm, '<h2 style="font-size:17px;font-weight:800;color:#0f172a;margin:0 0 10px">$1</h2>')
    // numbered lists
    .replace(/^\d+\. (.+)$/gm, '<li style="margin:3px 0">$1</li>')
    // bullet lists
    .replace(/^[-*] (.+)$/gm, '<li style="margin:3px 0">$1</li>')
    // wrap consecutive <li> runs in <ul>
    .replace(/(<li[^>]*>[\s\S]*?<\/li>\n?)+/g, (m) => `<ul style="padding-left:18px;margin:6px 0">${m}</ul>`)
    // paragraph breaks
    .replace(/\n\n/g, '</p><p style="margin:6px 0">')
    .replace(/\n/g, '<br>');
  return `<p style="margin:6px 0">${html}</p>`;
}

// ── localStorage helpers (cache; server draft is source of truth) ────────
function lsKey(sceneId) { return `codelab_code_${sceneId}`; }
function lsLoad(sceneId, fallback) {
  try { return localStorage.getItem(lsKey(sceneId)) || fallback; }
  catch { return fallback; }
}
function lsSave(sceneId, code) {
  try { localStorage.setItem(lsKey(sceneId), code); } catch { /* non-critical */ }
}

const DRAFT_DEBOUNCE_MS = 1200;

/** True when the lab needs packages JupyterLite cannot provide. */
function labRequiresExternalPackages(scene, meta, starterCode) {
  const flag =
    scene?.requires_external_packages ??
    scene?.scene_data?.requires_external_packages ??
    scene?.metadata?.requires_external_packages ??
    meta?.requires_external_packages;
  if (flag === true) return true;
  if (flag === false) return false;
  const code = starterCode || '';
  // Heuristic: known non-Pyodide / non-stdlib imports used in AI packs.
  return /\b(?:import|from)\s+(langchain|langgraph|crewai|openai|transformers|torch|tensorflow)\b/i.test(code);
}

export default function CodeLabPlayer({ scene, domain, learnerId, onContextChange }) {
  const sceneId = scene.scene_id || scene.segment_id;

  // For published tutorial segments, all fields are at the root level
  // (flattened by publish_tutorials.py). For direct scene objects (coach preview),
  // they live in domain_metadata. We check both shapes.
  const meta = scene.domain_metadata || scene.scene_data?.domain_metadata || scene;

  // sandbox_provider is at the root for published segments; fall through to meta.
  const sandboxProvider =
    scene.sandbox_provider ||           // published tutorial segments
    scene.meta?.sandbox_provider ||
    scene.metadata?.sandbox_provider ||
    meta.sandbox_provider ||
    'local';
  const useExternalSandbox = sandboxProvider === 'stackblitz' || sandboxProvider === 'jupyterlite';
  const isJupyterLite = sandboxProvider === 'jupyterlite';

  // Prefer domain_metadata for instructions + starter so tutorial segment
  // root copies cannot silently diverge from the authored scene payload.
  const instructionsMd =
    meta.instructions_md ||
    meta.instructions ||
    scene.instructions ||
    scene.instructions_md ||

    '';

  const starterCode =
    meta.starter_code ||
    scene.starter_code ||
    '';

  const needsExternalPackages = labRequiresExternalPackages(scene, meta, starterCode);

  const sandboxConfig = sandboxProvider === 'stackblitz'
    ? {
        projectId: scene.scene_data?.sandbox_project_id || scene.metadata?.sandbox_project_id,
        src: scene.scene_data?.sandbox_src || scene.metadata?.sandbox_src,
        files: scene.scene_data?.sandbox_files || scene.metadata?.sandbox_files,
        template: scene.scene_data?.sandbox_template || scene.metadata?.sandbox_template || 'node',
        dependencies: scene.scene_data?.sandbox_dependencies || scene.metadata?.sandbox_dependencies,
      }
    : isJupyterLite
    ? {
        // JupyterLite REPL — kernel=python, toolbar visible so learner can run cells.
        // The /repl/ interface is simpler than the full /lab/ UI for learners.
        src: `https://jupyterlite.github.io/demo/repl/index.html?kernel=python&toolbar=1`,
      }
    : null;

  // Initial paint from localStorage cache; server draft rehydrates below.
  const [code, setCode] = useState(() => lsLoad(sceneId, starterCode));
  const [language] = useState(scene.language || meta.language || 'python');
  const [instructions] = useState(instructionsMd);
  const [testCases] = useState(scene.test_cases || meta.test_cases || []);
  const [testResults, setTestResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [hints, setHints] = useState([]);
  const [hintLoading, setHintLoading] = useState(false);
  const [hintMessage, setHintMessage] = useState('');
  const [expandedErrors, setExpandedErrors] = useState({});
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(null);
  const [sandboxFailed, setSandboxFailed] = useState(false);
  const [draftStatus, setDraftStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const [draftHydrated, setDraftHydrated] = useState(false);
  const timerRef = useRef(null);
  const draftTimerRef = useRef(null);
  const skipNextDraftSave = useRef(true); // skip autosave until hydrate settles
  const timeLimit = meta.time_limit_minutes;

  const persistCodeLocally = useCallback((next) => {
    lsSave(sceneId, next);
  }, [sceneId]);

  const saveDraftToServer = useCallback(async (sourceText) => {
    if (!sceneId || sourceText == null) return;
    setDraftStatus('saving');
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/draft`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          source_text: sourceText,
          language,
          sandbox_provider: sandboxProvider,
          domain: domain || undefined,
          tutorial_id: scene.tutorial_id || undefined,
          track_id: scene.track_id || undefined,
        }),
      });
      if (!res.ok) throw new Error(`draft HTTP ${res.status}`);
      setDraftStatus('saved');
    } catch {
      setDraftStatus('error');
    }
  }, [sceneId, language, sandboxProvider, domain, scene.tutorial_id, scene.track_id]);

  const updateCode = useCallback((next) => {
    setCode(next);
    persistCodeLocally(next);
  }, [persistCodeLocally]);

  // Rehydrate: server draft → last submission (API) → localStorage → starter
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/draft`, {
          headers: authHeaders(),
        });
        if (!res.ok || cancelled) {
          if (!cancelled) setDraftHydrated(true);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data?.found && typeof data.source_text === 'string' && data.source_text.length > 0) {
          setCode(data.source_text);
          persistCodeLocally(data.source_text);
          setDraftStatus('saved');
        }
      } catch { /* keep local cache */ }
      finally {
        if (!cancelled) {
          setDraftHydrated(true);
          setTimeout(() => { skipNextDraftSave.current = false; }, 400);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sceneId, persistCodeLocally]);

  // Debounced server draft autosave (Mongo code_lab_artifacts — not GitHub)
  useEffect(() => {
    if (!draftHydrated || skipNextDraftSave.current) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      saveDraftToServer(code);
    }, DRAFT_DEBOUNCE_MS);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [code, draftHydrated, saveDraftToServer]);

  // Publish live codelab state to the parent (TutorialPlayerPage) whenever
  // code, test results, or hints change so ChatWidget has full context.
  useEffect(() => {
    if (!onContextChange) return;
    onContextChange({
      scene_id: sceneId,
      sandbox_provider: sandboxProvider,
      language,
      current_code: code,
      last_test_results: testResults?.results || null,
      hints_used: hints.length,
      draft_status: draftStatus,
    });
  }, [code, testResults, hints, sceneId, sandboxProvider, language, onContextChange, draftStatus]);

  useEffect(() => {
    if (!timeLimit) return;
    const totalSec = timeLimit * 60;
    setTimer(totalSec);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timeLimit]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/submissions`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch { /* non-critical */ }
  }, [sceneId]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/run`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setTestResults(data);
    } catch (e) {
      setError(e.message || 'Failed to run code');
    } finally {
      setRunning(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/submit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          code,
          sandbox_provider: sandboxProvider,
          client_test_results: useExternalSandbox && testResults?.results
            ? testResults.results
            : undefined,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setLastSubmission(data);
      setSubmitted(true);
      setTestResults({
        results: data.test_results,
        passed: data.tests_passed,
        total: data.tests_total,
        all_passed: data.all_passed,
      });
      fetchSubmissions();
      if (data.all_passed) {
        emitLearnerEvent({
          event_type: 'code_lab_completed',
          scene_id: sceneId,
          language,
          sandbox_provider: sandboxProvider,
          score_pct: data.score_pct,
        }).catch(() => {});
      }
    } catch (e) {
      setError(e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHint = async () => {
    setHintLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/hint`, {
        method: 'POST',
        headers: authHeaders(),
        // Tell the backend how many hints the learner has already seen so it
        // returns the NEXT one rather than the last available one.
        body: JSON.stringify({ hints_shown: hints.length }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.hint) {
          const hint = typeof data.hint === 'string' ? { text: data.hint } : data.hint;
          setHints((prev) => [...prev, hint]);
        } else if (data.message) {
          // "Hints unlock after more attempts" — surface as a friendly note, not an error
          setHintMessage(data.message);
        }
      }
    } catch { /* non-critical */ }
    finally { setHintLoading(false); }
  };

  const formatTime = (sec) => {
    if (sec == null) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={langBadgeStyle}>{language.toUpperCase()}</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
            {isJupyterLite ? 'AI Notebook' : 'Code Lab'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {timer != null && (
            <span style={{
              ...timerStyle,
              color: timer < 60 ? '#dc2626' : timer < 300 ? '#d97706' : '#059669',
            }}>
              {formatTime(timer)}
            </span>
          )}
          {testResults && (
            <span style={{
              fontSize: 13, fontWeight: 600,
              color: testResults.all_passed ? '#059669' : '#dc2626',
            }}>
              {testResults.passed}/{testResults.total} passed
            </span>
          )}
        </div>
      </div>

      {/* Main split layout */}
      <div style={splitLayoutStyle}>
        {/* Left: Instructions */}
        <div style={instructionsPanelStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', marginBottom: 10 }}>
            Instructions
          </div>
          <div
            style={instructionsContentStyle}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: renderMarkdown(instructions) }}
          />

          {/* Starter Code Reference (JupyterLite) */}
          {isJupyterLite && starterCode && (
            <div style={starterCodeSectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b' }}>
                  Starter Code (reference)
                </span>
                <button
                  onClick={() => { navigator.clipboard.writeText(starterCode); }}
                  style={copyCodeBtnStyle}
                >
                  Copy to clipboard
                </button>
              </div>
              <pre style={starterCodeBlockStyle}>
                <code>{starterCode}</code>
              </pre>
              <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0', lineHeight: 1.5 }}>
                {needsExternalPackages ? (
                  <>
                    This code requires packages not available in the browser notebook.
                    Copy it and run it in your local Python environment, or use it as
                    a reference while exploring concepts in the notebook above.
                  </>
                ) : (
                  <>
                    This code runs with the Python standard library — no external
                    packages required. Paste it into the notebook (or edit in the
                    panel), then use <strong>Run Tests</strong> / complete the lab
                    when you are ready.
                  </>
                )}
              </p>
            </div>
          )}

          {/* TODO highlight — tell the learner where to make changes */}
          {starterCode && starterCode.includes('NotImplementedError') && (
            <div style={todoGuideStyle}>
              <span style={{ fontWeight: 700 }}>📝 What to do:</span> Find the lines that say{' '}
              <code style={{ background: '#fde68a', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>
                raise NotImplementedError
              </code>{' '}
              in the code editor and replace them with your implementation. Each one marks a section you need to complete.
            </div>
          )}

          {/* Hints */}
          {(meta.hints?.length > 0 || hints.length > 0) && (
            <div style={hintsSectionStyle}>
              {hints.length < (meta.hints?.length || 0) ? (
                <button
                  onClick={() => { setHintMessage(''); handleHint(); }}
                  disabled={hintLoading}
                  style={hintBtnStyle}
                >
                  {hintLoading ? 'Loading hint...' : hints.length === 0
                    ? `Get Hint (${meta.hints?.length || 0} available)`
                    : `Get Next Hint (${hints.length}/${meta.hints?.length || 0} shown)`}
                </button>
              ) : (
                <div style={{ fontSize: 12, color: '#92400e', padding: '6px 10px', background: '#fef3c7', borderRadius: 6 }}>
                  All {meta.hints?.length} hints shown. Try applying them or ask the coach for more help.
                </div>
              )}
              {hintMessage && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, padding: '6px 10px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  💡 {hintMessage}
                </div>
              )}
              {hints.map((h, i) => (
                <div key={i} style={hintCardStyle}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>Hint {i + 1}:</span>{' '}
                  <span style={{ fontSize: 13 }}>{h.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Code editor + results */}
        <div style={editorPanelStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Your Code</span>
            {draftStatus === 'saving' && (
              <span style={{ fontWeight: 500, textTransform: 'none', color: '#94a3b8' }} data-testid="codelab-draft-status">Saving…</span>
            )}
            {draftStatus === 'saved' && (
              <span style={{ fontWeight: 500, textTransform: 'none', color: '#86efac' }} data-testid="codelab-draft-status">Saved</span>
            )}
            {draftStatus === 'error' && (
              <span style={{ fontWeight: 500, textTransform: 'none', color: '#fca5a5' }} data-testid="codelab-draft-status">Save failed — retrying</span>
            )}
          </div>

          {useExternalSandbox && !sandboxFailed ? (
            <SandboxErrorBoundary
              onError={() => setSandboxFailed(true)}
              fallback={
                <div style={sandboxWarningStyle}>
                  Sandbox failed to load. Falling back to local editor.
                </div>
              }
            >
              <SandboxIframe
                provider={sandboxProvider}
                src={sandboxConfig.src}
                projectId={sandboxConfig?.projectId}
                files={sandboxConfig?.files}
                height={isJupyterLite ? 600 : 400}
                title={isJupyterLite ? `${language} Notebook` : `${language} sandbox`}
                onError={() => setSandboxFailed(true)}
                onMessage={(data) => {
                  if (data?.type === 'code_change' && data.code != null) {
                    updateCode(data.code);
                  }
                }}
              />
            </SandboxErrorBoundary>
          ) : (
            <>
              {sandboxFailed && (
                <div style={sandboxWarningStyle}>
                  {isJupyterLite ? 'JupyterLite' : 'StackBlitz'} sandbox unavailable — using local editor instead.
                </div>
              )}
              <textarea
                value={code}
                onChange={(e) => {
                  updateCode(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Tab inserts 2 spaces instead of moving focus
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    const start = e.target.selectionStart;
                    const end = e.target.selectionEnd;
                    const newCode = code.substring(0, start) + '  ' + code.substring(end);
                    updateCode(newCode);
                    requestAnimationFrame(() => {
                      e.target.selectionStart = start + 2;
                      e.target.selectionEnd = start + 2;
                    });
                  }
                }}
                spellCheck={false}
                style={codeEditorStyle}
                placeholder={`Write your ${language} code here...`}
                data-testid="codelab-editor"
              />
            </>
          )}

          {/* Action buttons */}
          <div style={actionBarStyle}>
            {!isJupyterLite && (
              <button onClick={handleRun} disabled={running || !code.trim()} style={runBtnStyle}>
                {running ? 'Running...' : 'Run Tests'}
              </button>
            )}
            <button
              onClick={isJupyterLite ? async () => {
                setSubmitted(true);
                setLastSubmission({ all_passed: true, score_pct: 100 });
                await emitLearnerEvent({
                  event_type: 'code_lab_completed',
                  scene_id: sceneId,
                  language,
                  sandbox_provider: sandboxProvider,
                });
              } : handleSubmit}
              disabled={!isJupyterLite && (submitting || !code.trim() || (timer != null && timer <= 0))}
              style={isJupyterLite ? markCompleteBtnStyle : submitBtnStyle}
            >
              {submitting ? 'Submitting...' : isJupyterLite ? 'Mark Complete' : 'Submit'}
            </button>
          </div>
          {isJupyterLite && (
            <div style={notebookHintStyle}>
              Run code directly in the notebook above. When done, click <strong>Mark Complete</strong> to record your progress.
            </div>
          )}

          {isJupyterLite && (
            <JupyterLiteGuide needsExternalPackages={needsExternalPackages} />
          )}

          {error && <div style={errorStyle}>{error}</div>}

          {/* Test results */}
          {testResults && (
            <div style={resultsPanelStyle}>
              <div style={{
                fontSize: 13, fontWeight: 700, marginBottom: 8,
                color: testResults.all_passed ? '#059669' : '#dc2626',
              }}>
                Test Results — {testResults.passed}/{testResults.total} passed
                {testResults.all_passed && ' — All tests passed!'}
              </div>
              <table style={resultsTableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Test</th>
                    <th style={thStyle}>Expected</th>
                    <th style={thStyle}>Actual</th>
                  </tr>
                </thead>
                <tbody>
                  {(testResults.results || []).map((r, i) => {
                    const rawError = r.error || '';
                    // Extract the last meaningful line of a traceback for the summary
                    const errorLines = rawError.split('\n').filter(l => l.trim());
                    const errorSummary = errorLines[errorLines.length - 1] || rawError;
                    const isLong = rawError.length > 60 || rawError.includes('\n');
                    const isExpanded = expandedErrors[i];
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fafbfc' : '#fff' }}>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 16 }}>{r.passed ? '\u2705' : '\u274C'}</span>
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{r.name}</td>
                        <td style={tdCodeStyle}>{r.expected_output || ''}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: '#475569', maxWidth: 260 }}>
                          {rawError ? (
                            <div>
                              <span style={{ color: '#dc2626' }}>
                                {isExpanded ? '' : errorSummary.slice(0, 80)}
                              </span>
                              {isLong && !isExpanded && (
                                <button
                                  onClick={() => setExpandedErrors(prev => ({ ...prev, [i]: true }))}
                                  style={showFullErrBtnStyle}
                                >
                                  Show full error ▾
                                </button>
                              )}
                              {isExpanded && (
                                <pre style={fullErrorStyle}>{rawError}</pre>
                              )}
                              {isExpanded && (
                                <button
                                  onClick={() => setExpandedErrors(prev => ({ ...prev, [i]: false }))}
                                  style={showFullErrBtnStyle}
                                >
                                  Collapse ▴
                                </button>
                              )}
                            </div>
                          ) : (
                            r.actual_output || ''
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Submission result toast */}
          {submitted && lastSubmission && (
            <div style={{
              ...resultsPanelStyle,
              background: lastSubmission.all_passed
                ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
                : 'linear-gradient(135deg, #fef2f2, #fecaca)',
              border: `1px solid ${lastSubmission.all_passed ? '#a7f3d0' : '#fca5a5'}`,
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: lastSubmission.all_passed ? '#065f46' : '#991b1b' }}>
                {lastSubmission.all_passed ? 'All tests passed! Submission recorded.' : `Score: ${lastSubmission.score_pct}%`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submission history */}
      {submissions.length > 0 && (
        <div style={historySectionStyle}>
          <button onClick={() => setShowHistory((v) => !v)} style={historyToggleBtnStyle}>
            {showHistory ? '\u25BE' : '\u25B8'} Submission History ({submissions.length})
          </button>
          {showHistory && (
            <div style={{ marginTop: 8 }}>
              {submissions.map((sub) => (
                <div key={sub.submission_id} style={historyCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: sub.all_passed ? '#059669' : '#dc2626',
                    }}>
                      {sub.all_passed ? 'PASSED' : 'FAILED'} — {sub.tests_passed}/{sub.tests_total} tests
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      {new Date(sub.submitted_at).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Score: {sub.score_pct}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  borderRadius: 12,
  overflow: 'hidden',
  border: '1px solid #e2e8f0',
  background: '#fff',
};

const headerBarStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 16px',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  color: '#e2e8f0',
};

const langBadgeStyle = {
  background: '#3b82f6',
  color: '#fff',
  fontSize: 10,
  fontWeight: 800,
  padding: '3px 8px',
  borderRadius: 4,
  letterSpacing: 1,
};

const timerStyle = {
  fontFamily: 'monospace',
  fontSize: 16,
  fontWeight: 700,
};

const splitLayoutStyle = {
  display: 'flex',
  gap: 0,
  minHeight: 400,
};

const instructionsPanelStyle = {
  flex: '0 0 40%',
  padding: '16px 18px',
  borderRight: '1px solid #e2e8f0',
  overflowY: 'auto',
  maxHeight: 600,
  background: '#fafbfc',
};

const instructionsContentStyle = {
  fontSize: 13.5,
  lineHeight: 1.65,
  color: '#1e293b',
};

const editorPanelStyle = {
  flex: 1,
  padding: '16px 18px',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const codeEditorStyle = {
  flex: 1,
  minHeight: 220,
  fontFamily: "'Fira Code', 'Cascadia Code', 'JetBrains Mono', 'Menlo', monospace",
  fontSize: 13,
  lineHeight: 1.6,
  padding: 14,
  border: '1px solid #d1d5db',
  borderRadius: 8,
  background: '#0f172a',
  color: '#e2e8f0',
  resize: 'vertical',
  boxSizing: 'border-box',
  tabSize: 4,
  whiteSpace: 'pre',
  overflowWrap: 'normal',
  overflowX: 'auto',
};

const actionBarStyle = {
  display: 'flex',
  gap: 8,
  marginTop: 10,
  marginBottom: 10,
};

const runBtnStyle = {
  background: '#1e293b',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const submitBtnStyle = {
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const errorStyle = {
  color: '#dc2626',
  fontSize: 12,
  padding: '6px 10px',
  background: '#fef2f2',
  borderRadius: 6,
  marginBottom: 8,
};

const resultsPanelStyle = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 12,
  marginTop: 8,
};

const resultsTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const thStyle = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '2px solid #e2e8f0',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#64748b',
};

const tdStyle = {
  padding: '6px 8px',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'top',
};

const tdCodeStyle = {
  ...tdStyle,
  fontFamily: 'monospace',
  fontSize: 11,
  color: '#475569',
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const hintsSectionStyle = {
  marginTop: 16,
  paddingTop: 12,
  borderTop: '1px solid #e2e8f0',
};

const hintBtnStyle = {
  background: '#fef3c7',
  color: '#92400e',
  border: '1px solid #fde68a',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  marginBottom: 8,
};

const hintCardStyle = {
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 6,
  padding: '8px 10px',
  marginTop: 6,
  lineHeight: 1.5,
  color: '#78350f',
};

const sandboxWarningStyle = {
  color: '#92400e',
  fontSize: 12,
  padding: '8px 12px',
  background: '#fef3c7',
  border: '1px solid #fde68a',
  borderRadius: 6,
  marginBottom: 8,
};

const historySectionStyle = {
  padding: '12px 18px',
  borderTop: '1px solid #e2e8f0',
  background: '#fafbfc',
};

const historyToggleBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700,
  color: '#3b82f6',
  padding: 0,
};

const historyCardStyle = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  padding: '8px 10px',
  marginBottom: 6,
};

const markCompleteBtnStyle = {
  background: '#7c3aed',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const notebookHintStyle = {
  fontSize: 12,
  color: '#475569',
  background: '#f0f9ff',
  border: '1px solid #bae6fd',
  borderRadius: 6,
  padding: '8px 12px',
  marginBottom: 8,
  lineHeight: 1.5,
};

const starterCodeSectionStyle = {
  marginTop: 16,
  paddingTop: 12,
  borderTop: '1px solid #e2e8f0',
};

const starterCodeBlockStyle = {
  background: '#1e1e1e',
  color: '#d4d4d4',
  borderRadius: 8,
  padding: '14px 16px',
  fontSize: 12,
  lineHeight: 1.65,
  fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const copyCodeBtnStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 5,
  padding: '4px 12px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

const guideContainerStyle = {
  border: '1px solid #c7d2fe',
  borderRadius: 8,
  background: '#eef2ff',
  marginBottom: 8,
  overflow: 'hidden',
};

const guideToggleStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700,
  color: '#4338ca',
  padding: '10px 14px',
  width: '100%',
  textAlign: 'left',
};

const guideContentStyle = {
  padding: '0 14px 14px',
  fontSize: 13,
  lineHeight: 1.6,
  color: '#1e293b',
};

const guideSection = {
  marginBottom: 14,
};

const guideSectionTitle = {
  fontWeight: 700,
  fontSize: 13,
  color: '#3730a3',
  marginBottom: 6,
};

const guideListStyle = {
  margin: '4px 0',
  paddingLeft: 20,
};

const guideCodeInline = {
  background: '#e0e7ff',
  padding: '1px 5px',
  borderRadius: 3,
  fontFamily: 'monospace',
  fontSize: 12,
};

const guideCodeBlock = {
  background: '#1e1e1e',
  color: '#d4d4d4',
  borderRadius: 6,
  padding: '12px 14px',
  fontSize: 12,
  lineHeight: 1.6,
  fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
  overflowX: 'auto',
  whiteSpace: 'pre',
  margin: '6px 0 0',
};

const guideTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const guideKeyCell = {
  padding: '4px 8px',
  borderBottom: '1px solid #c7d2fe',
  width: 160,
};

const todoGuideStyle = {
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 6,
  padding: '10px 12px',
  fontSize: 13,
  lineHeight: 1.55,
  color: '#78350f',
  marginTop: 14,
  marginBottom: 4,
};

const showFullErrBtnStyle = {
  display: 'block',
  marginTop: 3,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#dc2626',
  fontSize: 11,
  fontWeight: 600,
  padding: 0,
  textDecoration: 'underline',
};

const fullErrorStyle = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 4,
  padding: '8px 10px',
  fontSize: 11,
  lineHeight: 1.5,
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  color: '#dc2626',
  marginTop: 4,
  maxHeight: 200,
  overflowY: 'auto',
};

const kbdStyle = {
  background: '#fff',
  border: '1px solid #a5b4fc',
  borderRadius: 4,
  padding: '2px 6px',
  fontFamily: 'monospace',
  fontSize: 11,
  fontWeight: 600,
};
